//*Libraries imports
import { useEffect, useState, useRef } from "react";
import { Search, Pencil, Copy } from "lucide-react";
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Button as MuiButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NoteIcon from "@mui/icons-material/Note";
import DownloadIcon from "@mui/icons-material/Download";
import FileIcon from "@mui/icons-material/InsertDriveFile";

//*Components imports
import { DocumentCard } from "./components/ActivityModal/DocumentCard";
import { CopyFormButton } from "./components/CopyFormButton";
import { PdfViewer } from "./components/ActivityModal/PdfViewer";
import { SpreadsheetViewer } from "./components/ActivityModal/SpreadsheetViewer";
import FilledFormForm from "../filledForm/FilledFormForm";

//*Types Imports
import { PhaseFormFieldType } from "../phaseForm";
import { IProduct } from "../product/types";
import {
  PhaseFormOutputDTO,
  PhaseFormFieldData,
  PhaseFormFieldOption,
} from "../phaseForm/types";
import { ClientOutputDTO } from "../Client/types";
import {
  Person,
  FileData,
  AssetData,
  ProcessWithFilledForms,
  ExtendedPhaseForm,
} from "@/schemas";

//*Config imports
import { API_URL } from "@/config/api";

function dynamicSort(property: string) {
  let sortOrder = 1;
  let prop = property;
  if (prop[0] === "-") {
    sortOrder = -1;
    prop = prop.substring(1);
  }
  return function (a: Record<string, unknown>, b: Record<string, unknown>) {
    const valA = (a[prop] as string | number) ?? "";
    const valB = (b[prop] as string | number) ?? "";
    const result = valA < valB ? -1 : valA > valB ? 1 : 0;
    return result * sortOrder;
  };
}

interface DadosDosFormulariosProps {
  product: Partial<IProduct> & { _id: string; name: string };
  processes: ProcessWithFilledForms[];
  phaseForms: Record<string, PhaseFormOutputDTO>;
  client?: ClientOutputDTO & { phone?: string };
  documents?: FileData[];
  docsFilled?: Record<string, string | number | boolean | object | null>;
  products?: PhaseFormFieldOption[];
  loading?: boolean;
  loadingUploads?: Record<string, boolean>;
  onUploadFile?: (docId: string, file: File) => void;
  onDownload?: (path: string) => void;
  onReload?: () => Promise<void> | void;
}

export default function DadosDosFormularios(props: DadosDosFormulariosProps) {
  const [search, setSearch] = useState("");
  const [search_2, setSearch2] = useState("");

  const [_filter, setFilter] = useState("all");
  const [products, setProducts] = useState<PhaseFormFieldOption[]>(
    props.products || [],
  );

  const [editedValues, _setEditedValues] = useState<
    Record<string, string | number | boolean | object | null>
  >({});
  const [isEditingForm, setIsEditingForm] = useState<boolean>(false);
  const [isCreatingForm, setIsCreatingForm] = useState<boolean>(false);
  const [filledFormsID, setFilledFormsID] = useState<string>("");

  const [selectForm, setSelectForm] = useState<ExtendedPhaseForm | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [spreadsheetPreviewUrl, setSpreadsheetPreviewUrl] = useState<
    string | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedDocRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      setSelectForm(null);
      setFilledFormsID("");
      console.log("DEMONTOU");
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && selectedDocRef.current) {
      if (props.onUploadFile) {
        props.onUploadFile(selectedDocRef.current, e.target.files[0]);
      }
    }
    e.target.value = "";
  };

  const resolveUploadTarget = () => {
    let phaseFormId = "";
    let processId = "";

    if (props.processes && props.product?._id) {
      props.processes.forEach((item3: ProcessWithFilledForms) => {
        item3.filledForms?.forEach((item4) => {
          item4.data?.forEach((item5) => {
            if (item5.field.type == PhaseFormFieldType.BINDING_QUESTION) {
              const bindingValues = Array.isArray(item5?.data)
                ? (item5.data as string[])
                : item5?.data != null
                  ? [String(item5.data)]
                  : [];
              bindingValues.forEach((item6: string) => {
                if (item6 == props.product._id) {
                  processId = item3._id;
                  phaseFormId = item4.phaseFormId;
                }
              });
            }
          });
        });
      });
    }

    if (!processId && props.processes?.length > 0) {
      const p = props.processes[0];
      processId = p._id;
      phaseFormId = p.filledForms?.[0]?.phaseFormId || "";
    }

    return { phaseFormId, processId };
  };

  const { phaseFormId, processId } = resolveUploadTarget();
  const canUploadDocuments = Boolean(
    phaseFormId && processId && props.onUploadFile,
  );

  const handleUploadClick = (docId: string) => {
    if (!canUploadDocuments) return;
    selectedDocRef.current = docId;
    fileInputRef.current?.click();
  };

  const handleDownload = (path: string) => {
    if (!path) return;
    const isPdf = /\.pdf(\?|#|$)/i.test(path);
    if (isPdf) {
      setPdfPreviewUrl(path);
      return;
    }

    const isSpreadsheet = /\.(xlsx|xls|csv)(\?|#|$)/i.test(path);
    if (isSpreadsheet) {
      setSpreadsheetPreviewUrl(path);
      return;
    }

    if (props.onDownload) {
      props.onDownload(path);
      return;
    }
    const link = document.createElement("a");
    link.href = path;
    link.target = "_blank";
    link.click();
  };

  const activityDocuments = Array.isArray(selectForm?.activity?.documents)
    ? selectForm.activity.documents
    : Object.values(selectForm?.activity?.documents || {});

  const loadData = async () => {
    if (props.products && props.products.length > 0) return;
    let keys = await (await fetch(API_URL + "/products/keys")).json();
    setProducts(keys);
  };

  const refReload = useRef(true);

  useEffect(() => {
    loadData();
    if (props.processes) {
      console.log("Processos recebidos:", props.processes);
    }
    if (Object.values(props.phaseForms).length > 0) {
      refReload.current = false;
      const firstForm = selectForm ?? Object.values(props.phaseForms)[0];
      console.log("firstform", firstForm);
      // handleFormSelection(firstForm, firstForm?.index ?? 0,false)
      let form: any = {
        ...firstForm,
        index: firstForm.index ? firstForm.index : 0,
      };
      console.log("second firstform", form);

      if (props.processes) {
        props.processes.map((process: ProcessWithFilledForms) => {
          const matchingForm = process.filledForms
            ?.sort(dynamicSort("-createdAt"))
            .filter((item2) => {
              if (item2._id == form._id || form._id == item2.phaseFormId) {
                return true;
              }
              return false;
            })[0];
          console.log("formsss", process.filledForms, form._id);
          if (matchingForm) {
            console.log("matching form", matchingForm);
            form = {
              ...matchingForm,
              index: firstForm.index ? firstForm.index : 0,
              activity: firstForm.activity,
              phase: firstForm.phase,
            };
            setFilledFormsID(matchingForm._id);
          }
        });
      }

      // console.log("form aqui:", form);
      setSelectForm(form);
    }

    return () => {
      refReload.current = true;
    };
  }, [props.processes]);

  const renderFileDownload = (file: FileData) => {
    if (!file) return null;

    // Função para formatar o tamanho do arquivo
    const formatFileSize = (bytes?: number) => {
      if (!bytes) return "";
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    const handleOpenFile = () => {
      if (file.path) {
        window.open(file.path, "_blank");
      }
    };

    const hasError = file.status === "error" || !!file.errorMessage;

    return (
      <Box
        key={file.id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <FileIcon color={hasError ? "error" : "primary"} fontSize="small" />
        <Box sx={{ flexGrow: 1 }}>
          <Box
            component="span"
            onClick={hasError ? undefined : handleOpenFile}
            sx={{
              display: "flex",
              alignItems: "center",
              color: hasError ? "error.main" : "primary.main",
              textDecoration: "none",
              cursor: hasError ? "default" : "pointer",
              "&:hover": {
                textDecoration: hasError ? "none" : "underline",
              },
            }}
          >
            <Typography>{file.fileName}</Typography>
          </Box>
          {file.fileSize && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(file.fileSize)}
            </Typography>
          )}
        </Box>
        {hasError ? (
          <Tooltip title={file.errorMessage || "Erro no upload"}>
            <Chip label="Erro" color="error" size="small" />
          </Tooltip>
        ) : (
          <DownloadIcon
            color="primary"
            fontSize="small"
            sx={{ cursor: "pointer" }}
            onClick={handleOpenFile}
          />
        )}
      </Box>
    );
  };

  const renderPerson = (person: Person | Person[] | string) => {
    if (!person) return null;

    // Se person for um array, renderizar cada item
    if (Array.isArray(person)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {person.map((p, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Pessoa {index + 1}:
              </Typography>
              {renderPerson(p)}
            </Box>
          ))}
        </Box>
      );
    }

    // Se person for uma string, tentar fazer parse de JSON ou exibir como texto
    if (typeof person === "string") {
      try {
        const parsedPerson = JSON.parse(person);
        return renderPerson(parsedPerson);
      } catch {
        return <Typography>{person}</Typography>;
      }
    }

    // Se person não for um objeto, exibir como string
    if (typeof person !== "object") {
      return <Typography>{String(person)}</Typography>;
    }

    const p = person as Person;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{}}>
          {p.name && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginTop: 1,
              }}
            >
              <PersonIcon color="primary" fontSize="small" />
              <Typography style={{ fontSize: 15 }}>
                <strong>Nome:</strong> {p.name}
              </Typography>
            </Box>
          )}

          {(p.cpf || p.cpfCnpj) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginTop: 1,
              }}
            >
              <NoteIcon color="primary" fontSize="small" />
              <Typography style={{ fontSize: 15 }}>
                <strong>CPF/CNPJ:</strong> {p.cpf || p.cpfCnpj}
              </Typography>
            </Box>
          )}

          {p.email && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginTop: 1,
              }}
            >
              <EmailIcon color="primary" fontSize="small" />
              <Typography style={{ fontSize: 15 }}>
                <strong>Email:</strong> {p.email}
              </Typography>
            </Box>
          )}

          {(p.celphone || p.whatsapp) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginTop: 1,
              }}
            >
              <PhoneIcon color="primary" fontSize="small" />
              <Typography style={{ fontSize: 15 }}>
                <strong>Telefone:</strong> {p.celphone || p.whatsapp}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Renderizar cônjuge */}
        {p.couple && (
          <Box
            sx={{
              mt: 2,
              ml: 4,
              p: 2,
              borderLeft: "2px dashed",
              borderColor: "primary.light",
              marginTop: 1,
            }}
          >
            <Typography
              style={{ fontSize: 15 }}
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
            >
              Cônjuge
            </Typography>
            <Box>{renderPerson(p.couple)}</Box>
          </Box>
        )}

        {/* Renderizar filhos */}
        {p.children && p.children.length > 0 && (
          <Box
            sx={{
              mt: 2,
              ml: 4,
              p: 2,
              borderLeft: "2px dashed",
              borderColor: "secondary.light",
              marginTop: 1,
            }}
          >
            <Typography
              style={{ fontSize: 15 }}
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "secondary.main", mb: 1 }}
            >
              Filhos
            </Typography>
            {p.children.map((child, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {renderPerson(child)}
              </Box>
            ))}
          </Box>
        )}

        {/* Renderizar representantes legais */}
        {p.legalRepresentatives && p.legalRepresentatives.length > 0 && (
          <Box
            sx={{
              mt: 2,
              ml: 4,
              p: 2,
              borderLeft: "2px dashed",
              borderColor: "info.light",
              marginTop: 1,
            }}
          >
            <Typography
              style={{ fontSize: 15 }}
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "info.main", mb: 1 }}
            >
              Representantes Legais
            </Typography>
            {p.legalRepresentatives.map((representative, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {renderPerson(representative)}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Função utilitária para detectar e renderizar diferentes tipos de dados
  const renderDataValue = (
    data: string | number | boolean | object | null | undefined,
    fieldName: string = "",
  ): React.ReactNode => {
    if (data === null || data === undefined) {
      return <Typography color="text.secondary">Nenhum valor</Typography>;
    }

    // Tentar fazer parse de JSON se for uma string que parece JSON
    if (
      typeof data === "string" &&
      (data.startsWith("{") || data.startsWith("["))
    ) {
      try {
        const parsedData = JSON.parse(data);
        return renderDataValue(parsedData, fieldName);
      } catch {
        // Se não conseguir fazer parse, continuar com a string original
      }
    }

    // Se for um array
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <Typography color="text.secondary">Lista vazia</Typography>;
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {data.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Item {index + 1}:
              </Typography>
              {renderDataValue(item, `${fieldName}[${index}]`)}
            </Box>
          ))}
        </Box>
      );
    }

    // Se for um objeto
    if (typeof data === "object" && data !== null) {
      const keys = Object.keys(data);

      if (keys.length === 0) {
        return <Typography color="text.secondary">Objeto vazio</Typography>;
      }

      // Tratamento especial para dados de pessoa
      const p = data as Record<string, unknown>;
      if (p.name || p.email || p.cpf || p.cpfCnpj || p.celphone || p.whatsapp) {
        return renderPerson(p as unknown as Person);
      }

      // Tratamento especial para dados de arquivo
      const f = data as Record<string, unknown>;
      if (f.path || f.fileName || f.fileSize) {
        return renderFileDownload(f as unknown as FileData);
      }

      // Tratamento especial para dados de bem (asset)
      const a = data as Record<string, unknown>;
      if (a.assetType || a.description || a.value || a.quantity) {
        const asset = a as AssetData;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {asset.assetType && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Tipo:
                </Typography>
                <Typography>
                  {typeof asset.assetType === "object" &&
                  asset.assetType !== null
                    ? asset.assetType.name
                    : asset.assetType}
                </Typography>
              </Box>
            )}
            {asset.description && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Descrição:
                </Typography>
                <Typography>{asset.description}</Typography>
              </Box>
            )}
            {asset.value && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Valor:
                </Typography>
                <Typography>
                  R${" "}
                  {Number(asset.value).toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            )}
            {asset.quantity && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Quantidade:
                </Typography>
                <Typography>{asset.quantity}</Typography>
              </Box>
            )}
          </Box>
        );
      }

      // Tratamento especial para dados de papel (role)
      if (data.processRole || data.role || data.roleName) {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.processRole && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>
                  {typeof data.processRole === "object" &&
                  data.processRole !== null
                    ? data.processRole.name
                    : data.processRole}
                </Typography>
              </Box>
            )}
            {data.role && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>
                  {typeof data.role === "object" && data.role !== null
                    ? data.role.name
                    : data.role}
                </Typography>
              </Box>
            )}
            {data.roleName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>{data.roleName}</Typography>
              </Box>
            )}
          </Box>
        );
      }

      // Tratamento especial para datas
      if (data.date || (typeof data === "string" && !isNaN(Date.parse(data)))) {
        const dateValue = data.date || data;
        // Ensure dateValue is properly converted to string before passing to formatDateTime
        const { date, time } = formatDateTime(String(dateValue));
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography>
              {date} às {time}
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {keys.map((key) => {
            const value = data[key];
            return (
              <Box
                key={key}
                sx={{
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ mb: 0.5 }}
                >
                  {key}:
                </Typography>
                {renderDataValue(value, `${fieldName}.${key}`)}
              </Box>
            );
          })}
        </Box>
      );
    }

    // Se for uma string
    if (typeof data === "string") {
      if (data.trim() === "") {
        return <Typography color="text.secondary">Texto vazio</Typography>;
      }

      // Verificar se é uma data válida (ISO string, timestamp, etc.)
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
        /^\d{4}-\d{2}-\d{2}$/, // Date only
        /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
      ];

      const isDate =
        datePatterns.some((pattern) => pattern.test(data)) ||
        !isNaN(Date.parse(data));

      if (isDate) {
        const { date, time } = formatDateTime(data);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography>
              {date} às {time}
            </Typography>
          </Box>
        );
      }

      return <Typography>{data}</Typography>;
    }

    // Se for um número
    if (typeof data === "number") {
      // Verificar se é um valor monetário (baseado no nome do campo)
      const moneyKeywords = [
        "valor",
        "preço",
        "money",
        "custo",
        "taxa",
        "comissão",
        "pagamento",
        "recebimento",
      ];
      const isMoneyField = moneyKeywords.some((keyword) =>
        fieldName.toLowerCase().includes(keyword),
      );

      if (isMoneyField) {
        return (
          <Typography>
            R${" "}
            {data.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        );
      }

      return <Typography>{data.toLocaleString()}</Typography>;
    }

    // Se for um booleano
    if (typeof data === "boolean") {
      return (
        <Chip
          label={data ? "Sim" : "Não"}
          color={data ? "success" : "default"}
          size="small"
        />
      );
    }

    // Para outros tipos, tentar converter para string
    return <Typography>{String(data)}</Typography>;
  };

  const formatDateTime = (
    dateString: string | Date,
  ): { date: string; time: string } => {
    if (!dateString) return { date: "N/A", time: "N/A" };

    try {
      // Convert to string if it's not already
      const dateStr = String(dateString);
      const date = new Date(dateStr);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return { date: "Data inválida", time: "Hora inválida" };
      }

      // Formatação da data: DD/MM/YYYY
      const formattedDate = date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      // Formatação da hora: HH:MM
      const formattedTime = date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return { date: "Data inválida", time: "Hora inválida" };
    }
  };

  const renderDoc = (field: FileData) => {
    if (!field.path) {
      return (
        <Typography color="text.secondary">Nenhum arquivo enviado</Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {renderFileDownload(field)}
      </Box>
    );
  };

  const onlyUnique = (
    value: string | number,
    index: number,
    array: (string | number)[],
  ) => {
    return array.indexOf(value) === index;
  };

  const renderFieldValue = (
    field: PhaseFormFieldData,
    data: string | number | boolean | object | null | undefined,
    index: number,
  ) => {
    if (!field) {
      return (
        <Typography color="text.secondary">Campo não encontrado</Typography>
      );
    }

    // Handle undefined data
    // if (data === undefined || data === null) {
    //   return <Typography color="text.secondary">Nenhum dado disponível</Typography>;
    // }

    const editedValue = editedValues[field._id];

    switch (field.type) {
      case PhaseFormFieldType.PERSON:
        if (!data) return null;

        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const personData = data.value || data;

        return (
          <div>
            {/* {field.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {field.description}
              </Typography>
            )} */}

            {/* Renderizar dados de pessoa usando a função utilitária */}
            <div sx={{}}>
              <p
                className={`text-sm ${data ? "text-gray-500" : "text-red-500"}`}
              >
                {String(index + 1).padStart(2, "0")} - Dados da pessoa:
              </p>
              {renderDataValue(personData, field.name)}
            </div>

            {/* Renderizar arquivos anexados se existirem */}
            {data.files && data.files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Arquivos Anexados:
                </Typography>
                {data.files.map((filePath: string, index: number) => (
                  <Box key={index}>
                    {renderFileDownload({
                      path: filePath,
                      fileName:
                        filePath.split("/").pop() || `Arquivo ${index + 1}`,
                    })}
                  </Box>
                ))}
              </Box>
            )}
          </div>
        );

      case PhaseFormFieldType.SELECT:
        // Encontrar a opção correspondente ao valor atual
        const currentOption = field.options?.find(
          (opt: PhaseFormFieldOption) => opt.value === data,
        );

        // Verificar se é múltipla seleção
        const isMultiline = field.multiline || false;

        if (isMultiline) {
          // Para múltipla seleção, data pode ser um array
          const selectedValues = Array.isArray(data)
            ? (data as (string | number)[])
            : data
              ? [data as string | number]
              : [];

          const getSelectedLabels = (values: (string | number)[]) => {
            return values
              .map((value) => {
                const option = field.options?.find(
                  (opt: PhaseFormFieldOption) => opt.value === value,
                );
                return option?.key || String(value);
              })
              .join(", ");
          };

          return (
            <div className="space-y-1">
              <div className="flex justify-between items-start gap-3">
                <p
                  className={`text-sm ${
                    selectedValues.length > 0 ? "text-gray-500" : "text-red-500"
                  }`}
                >
                  {String(index + 1).padStart(2, "0")} - {field?.name}:
                </p>
                <div className="flex gap-2">
                  {!selectedValues.length && (
                    <Pencil size={16} className="text-red-500" />
                  )}
                  <Copy size={16} className="text-gray-400" />
                </div>
              </div>

              {selectedValues.length > 0 && (
                <p className="text-gray-900 font-medium">
                  {getSelectedLabels(selectedValues)}
                </p>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  currentOption?.key ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!currentOption?.key && (
                  <Pencil size={16} className="text-red-500" />
                )}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {currentOption?.key && (
              <p className="text-gray-900 font-medium">{currentOption?.key}</p>
            )}
          </div>
        );

      case PhaseFormFieldType.NUMBER:
        const numberData =
          typeof data === "object" && data !== null && "value" in data
            ? (data as { value: string | number }).value
            : (data as string | number);

        const formatNumber = (value: string | number | null | undefined) => {
          if (value === null || value === undefined || value === "")
            return "Nenhum valor";
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return numValue.toLocaleString("pt-BR");
        };

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  numberData ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!numberData && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {numberData && (
              <p className="text-gray-900 font-medium">
                {formatNumber(numberData)}
              </p>
            )}
          </div>
        );

      case PhaseFormFieldType.TEXT:
      case PhaseFormFieldType.STRING:
        const textData =
          typeof data === "object" && data !== null && "value" in data
            ? (data as { value: string | number }).value
            : (data as string | number);

        const formatText = (value: string | number | null | undefined) => {
          if (value === null || value === undefined || value === "")
            return "Nenhum texto";
          return String(value);
        };

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  textData ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!textData && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {textData && (
              <p className="text-gray-900 font-medium">
                {formatText(textData)}
              </p>
            )}
          </div>
        );

      case PhaseFormFieldType.UPLOAD:
      case PhaseFormFieldType.INVOICE:
        // if (!data) {
        //   return <Typography color="text.secondary">Nenhum arquivo enviado</Typography>;
        // }

        // if (Array.isArray(data)) {
        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  data?.length > 0 ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!data?.length && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {Array.isArray(data) && data.length > 0 && (
              <p className="text-gray-900 font-medium">
                {data.map((file: FileData) => renderFileDownload(file))}
              </p>
            )}
          </div>
        );
      // }
      // return <Typography color="text.secondary">Nenhum arquivo enviado</Typography>;

      case PhaseFormFieldType.PAGE:
        return null;

      case PhaseFormFieldType.ASSET:
      case PhaseFormFieldType.ROLE:
        // Para outros tipos, usar a função utilitária para renderizar corretamente
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const fieldData = data?.value || data;

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  fieldData ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!fieldData && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {fieldData && (
              <p className="text-gray-900 font-medium">
                {renderDataValue(fieldData, field.name)}
              </p>
            )}
          </div>
        );

      case PhaseFormFieldType.DATE:
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const dateData = data?.value || data;

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  dateData ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!dateData && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {dateData && (
              <p className="text-gray-900 font-medium">
                {formatDateTime(dateData).date}
              </p>
            )}
          </div>
        );

      case PhaseFormFieldType.MONEY:
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const moneyData = data?.value || data;
        const currentMoney =
          editedValue !== undefined ? editedValue : moneyData;

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  currentMoney ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!currentMoney && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {currentMoney && (
              <p className="text-gray-900 font-medium">{currentMoney}</p>
            )}
          </div>
        );
      case PhaseFormFieldType.BINDING_QUESTION:
        // Para qualquer tipo não tratado especificamente, usar a função utilitária
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const selectedValues = Array.isArray(data) ? data : data ? [data] : [];
        const selectedEditedValues = [
          ...selectedValues,
          ...(Array.isArray(editedValue)
            ? editedValue
            : editedValue
              ? [editedValue]
              : []),
        ].filter(onlyUnique);
        console.log(selectedValues, selectedEditedValues);
        const getSelectedLabels = (values: (string | number)[]) => {
          return values
            .map((value) => {
              const option = products?.find(
                (opt: PhaseFormFieldOption) => opt.value === value,
              );
              return option?.key || String(value);
            })
            .join(", ");
        };

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  selectedValues.length > 0 ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {selectedValues.length == 0 && (
                  <Pencil size={16} className="text-red-500" />
                )}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {selectedValues.length > 0 && (
              <p className="text-gray-900 font-medium">
                {getSelectedLabels(selectedValues)}
              </p>
            )}
          </div>
        );

      default:
        // Para qualquer tipo não tratado especificamente, usar a função utilitária
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const defaultData = data?.value || data;

        return (
          <div className="space-y-1">
            <div className="flex justify-between items-start gap-3">
              <p
                className={`text-sm ${
                  defaultData ? "text-gray-500" : "text-red-500"
                }`}
              >
                {String(index + 1).padStart(2, "0")} - {field?.name}:
              </p>
              <div className="flex gap-2">
                {!defaultData && <Pencil size={16} className="text-red-500" />}
                <Copy size={16} className="text-gray-400" />
              </div>
            </div>

            {defaultData && (
              <p className="text-gray-900 font-medium">{defaultData}</p>
            )}
          </div>
        );
    }
  };

  const handleFormSelection = async (
    form: PhaseFormOutputDTO,
    index: number,
    reload = true,
  ) => {
    const extendedForm: ExtendedPhaseForm = { ...form, index };
    setSelectForm(extendedForm);

    let currentFilledFormsID = "";
    setFilledFormsID("");
    setIsEditingForm(false);

    if (props.processes) {
      console.log("proccess");
      props.processes.map((item: ProcessWithFilledForms) => {
        if (item.filledForms) {
          const matchingForm = item.filledForms
            .sort(dynamicSort("-createdAt"))
            .filter((item2) => {
              if (item2._id == form._id || item2.phaseFormId == form._id) {
                return true;
              }
              return false;
            })[0];
          console.log("formsss", item.filledForms, form._id);
          if (matchingForm) {
            console.log("matchs", matchingForm);
            currentFilledFormsID = matchingForm._id;
            setFilledFormsID(matchingForm._id);
          }
        }
      });
    }

    if (!currentFilledFormsID && reload == true) {
      console.log(
        "Formulário não preenchido. Verificando possibilidade de criação automática...",
        form,
      );

      // Verificar se existe campo com tag "ID-CLIENTE"
      const idClienteField = form.formFields?.find(
        (f: PhaseFormFieldData) => f.tag === "ID-CLIENTE",
      );

      if (idClienteField && props.client) {
        console.log(
          "Campo ID-CLIENTE encontrado e cliente disponível. Criando formulário...",
        );
        setIsCreatingForm(true);

        try {
          const clientPhone = props.client.phone || props.client.whatsapp || "";

          if (!clientPhone) {
            console.warn(
              "Cliente sem telefone, não é possível criar formulário automático.",
            );
            setIsCreatingForm(false);
            return;
          }

          const applyMask = (value: string | number, mask: string) => {
            if (!mask) return String(value);
            let i = 0;
            const v = value.toString().replace(/\D/g, "");
            return mask.replace(/X/g, (_) => (i < v.length ? v[i++] : ""));
          };

          const formData =
            form.formFields?.map((field: PhaseFormFieldData) => {
              if (field.tag === "ID-CLIENTE") {
                let data = String(clientPhone);
                if (field.mask) {
                  data = applyMask(clientPhone, field.mask);
                }
                return {
                  field,
                  data,
                };
              }
              return {
                field,
                data: "",
              };
            }) || [];

          const payload = {
            phaseFormId: form._id,
            processId: "new",
            data: formData,
            status: "CLOSED",
          };

          const response = await fetch(API_URL + "/filledforms", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token") || "",
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            const newFilledForm = await response.json();
            console.log("Formulário criado com sucesso:", newFilledForm);

            if (newFilledForm._id) {
              setFilledFormsID(newFilledForm._id);
              // Recarregar dados para atualizar a lista
              if (props.onReload) await props.onReload();
              // Abrir modo de edição
              // setIsEditingForm(true);
            }
          } else {
            console.error("Erro ao criar formulário:", await response.text());
          }
        } catch (error) {
          console.error(
            "Erro ao processar criação automática de formulário:",
            error,
          );
        } finally {
          setIsCreatingForm(false);
        }
      }
    }
  };

  const handleCreateFormManual = async () => {
    if (!selectForm || !selectForm._id) return;

    setIsCreatingForm(true);
    try {
      const clientPhone = props.client?.phone || props.client?.whatsapp;

      const applyMask = (value: string, mask: string) => {
        if (!mask) return value;
        let i = 0;
        const v = value.toString().replace(/\D/g, "");
        return mask.replace(/X/g, (_) => (i < v.length ? v[i++] : ""));
      };

      const formData =
        selectForm.formFields?.map((field) => {
          if (field.tag === "ID-CLIENTE" && clientPhone) {
            let data = clientPhone;
            if (field.mask) {
              data = applyMask(clientPhone, field.mask);
            }
            return {
              field,
              data,
            };
          }
          return {
            field,
            data: "",
          };
        }) || [];

      const payload = {
        phaseFormId: selectForm._id,
        processId: "new",
        data: formData,
        status: "CLOSED",
      };

      const response = await fetch(API_URL + "/filledforms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token") || "",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const newFilledForm = await response.json();
        if (newFilledForm._id) {
          setFilledFormsID(newFilledForm._id);
          if (props.onReload) await props.onReload();
          setIsEditingForm(true);
        }
      } else {
        console.error(
          "Erro ao criar formulário manual:",
          await response.text(),
        );
      }
    } catch (error) {
      console.error("Erro ao criar formulário manual:", error);
    } finally {
      setIsCreatingForm(false);
    }
  };

  console.log("forms", props.phaseForms, filledFormsID);

  return (
    <div className="flex justify-around bg-gray-100">
      {Object.values(props.phaseForms).length == 0 && (
        <div
          className="bg-white rounded-2xl shadow-sm w-full p-8 space-y-6"
          style={{ height: "fit-content" }}
        >
          <p style={{ marginTop: 20, textAlign: "center" }}>
            Nenhum formulário encontrado
          </p>
        </div>
      )}
      {Object.values(props.phaseForms).length > 0 && (
        <>
          <div
            className="bg-gray-100 flex justify-center p-6"
            style={{ overflow: "scroll", height: "700px", width: "50%" }}
          >
            <div
              className="bg-white rounded-2xl shadow-sm w-full p-8 space-y-6"
              style={{ height: "fit-content", minHeight: "100%" }}
            >
              {/* Header */}
              {pdfPreviewUrl ? (
                <PdfViewer
                  url={pdfPreviewUrl}
                  onClose={() => setPdfPreviewUrl(null)}
                  title="Prévia do Documento"
                />
              ) : spreadsheetPreviewUrl ? (
                <SpreadsheetViewer
                  url={spreadsheetPreviewUrl}
                  onClose={() => setSpreadsheetPreviewUrl(null)}
                  title="Prévia da Planilha"
                />
              ) : (
                <div className="text-center space-y-2">
                  <p className="text-blue-600">Clientes</p>
                  <h1 className="text-2xl font-semibold">
                    Dados dos Formulários
                  </h1>

                  <div className="flex justify-center gap-2 mt-2">
                    {Object.values(props.phaseForms).map(
                      (s: PhaseFormOutputDTO, index) => (
                        <span
                          key={index}
                          onClick={() => handleFormSelection(s, index)}
                          style={{ cursor: "pointer" }}
                          className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium ${
                            index === selectForm?.index
                              ? "bg-blue-600 text-white"
                              : "text-gray-500"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      ),
                    )}
                  </div>

                  <p className="text-gray-500 mt-2">
                    {props.product.name} / {selectForm?.phase?.name} /{" "}
                    {selectForm?.activity?.name}
                  </p>
                  <p
                    style={{ textAlign: "center", cursor: "pointer" }}
                    className="text-gray-500"
                  >
                    {selectForm?.name
                      ? selectForm?.name
                      : selectForm?.phaseFormName}
                  </p>
                  <CopyFormButton
                    selectForm={selectForm}
                    productId={props.product._id}
                    clientId={
                      props.client?._id || props.processes?.[0]?.clientId
                    }
                  />
                </div>
              )}

              <hr />

              {!pdfPreviewUrl && !spreadsheetPreviewUrl && (
                <>
                  {/* Status */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setFilter("all")}
                      className="flex-1 border rounded-xl py-3"
                    >
                      Total |{" "}
                      {selectForm?.formFields
                        ? selectForm?.formFields?.length
                        : selectForm?.data?.length}
                    </button>
                    <button
                      onClick={() => setFilter("filled")}
                      className="flex-1 border rounded-xl py-3 text-blue-600"
                    >
                      Preenchidos |{" "}
                      {selectForm?.formFields
                        ? 0
                        : selectForm?.data?.filter((item) => item.data)?.length}
                    </button>
                    <button
                      onClick={() => setFilter("missing")}
                      className="flex-1 border rounded-xl py-3 text-red-500 flex justify-center gap-2 items-center"
                    >
                      Faltantes:{" "}
                      {selectForm?.formFields
                        ? selectForm?.formFields?.length
                        : selectForm?.data?.filter((item) => !item.data)
                            ?.length}
                    </button>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      placeholder="Pesquisar aqui..."
                      className="w-full border rounded-xl py-3 pl-12 pr-4"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {/* Lista */}
                  <div className="space-y-5  pr-2">
                    {props.loading ? (
                      <Box
                        sx={{ display: "flex", justifyContent: "center", p: 4 }}
                      >
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      <>
                        {selectForm?.formFields &&
                          selectForm?.formFields
                            ?.filter((item) => item?.name?.includes(search))
                            ?.map((q, index) => (
                              <div key={index} className="space-y-1">
                                <div className="flex justify-between items-start gap-3">
                                  <p
                                    className={`text-sm ${
                                      q.data ? "text-gray-500" : "text-red-500"
                                    }`}
                                  >
                                    {String(index + 1).padStart(2, "0")} -{" "}
                                    {q.name}:
                                  </p>
                                  <div className="flex gap-2">
                                    {!q.data && (
                                      <Pencil
                                        size={16}
                                        className="text-red-500"
                                      />
                                    )}
                                    {/* <Copy hidden size={16} className="text-gray-400" /> */}
                                  </div>
                                </div>

                                {q.data && (
                                  <p className="text-gray-900 font-medium">
                                    {q.data}
                                  </p>
                                )}
                              </div>
                            ))}

                        {selectForm?.data &&
                          selectForm?.data
                            ?.filter((item) =>
                              item?.field?.name?.includes(search),
                            )
                            ?.map((q, index) => (
                              <div key={index} className="space-y-1">
                                {/* {renderFieldValue(q.field, q.data)} */}
                                {renderFieldValue(q.field, q.data, index)}

                                {/* <div className="flex justify-between items-start gap-3">
                  <p
                    className={`text-sm ${q.data ? "text-gray-500" : "text-red-500"
                      }`}
                  >
                    {String(index + 1).padStart(2, "0")} - {q?.field?.name}:
                  </p>
                  <div className="flex gap-2">
                    {!q.data && (
                      <Pencil  hidden size={16} className="text-red-500" />
                    )}
                    <Copy hidden size={16} className="text-gray-400" />
                  </div>
                </div>

                {q.data && (
                  <p className="text-gray-900 font-medium">{q.data}</p>
                )} */}
                              </div>
                            ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div
            className="bg-gray-100 flex justify-center p-6"
            style={{ overflow: "scroll", height: "700px", width: "50%" }}
          >
            <div
              className="bg-white rounded-2xl shadow-sm w-full p-8 space-y-6"
              style={{ height: "fit-content", minHeight: "100%" }}
            >
              {/* Header */}
              <div
                className="text-center space-y-2"
                style={{ height: "fit-content", minHeight: "100%" }}
              >
                <p className="text-red-600">Operacional</p>
                <h1 className="text-2xl font-semibold">
                  Dados dos Formulários
                </h1>

                <div className="flex justify-center gap-2 mt-2">
                  {Object.values(props.phaseForms).map(
                    (s: PhaseFormOutputDTO, index) => (
                      <span
                        key={index}
                        onClick={() => {
                          handleFormSelection(s, index);
                        }}
                        style={{ cursor: "pointer" }}
                        className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium ${
                          index === selectForm?.index
                            ? "bg-blue-600 text-white"
                            : "text-gray-500"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    ),
                  )}
                </div>

                <p className="text-gray-500 mt-2">
                  {props.product.name} / {selectForm?.phase?.name} /{" "}
                  {selectForm?.activity?.name}
                </p>
                <p
                  style={{ textAlign: "center", cursor: "pointer" }}
                  className="text-gray-500"
                >
                  {selectForm?.name
                    ? selectForm?.name
                    : selectForm?.phaseFormName}
                </p>
                <CopyFormButton
                  selectForm={selectForm}
                  productId={props.product._id}
                  clientId={props.client?._id || props.processes?.[0]?.clientId}
                />
                {selectForm?._id && (
                  <p style={{ textAlign: "center" }}>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (filledFormsID) {
                          setIsEditingForm(true);
                        } else {
                          handleCreateFormManual();
                        }
                      }}
                      startIcon={<Pencil size={14} />}
                      sx={{ mt: 1, borderRadius: "8px", textTransform: "none" }}
                    >
                      {filledFormsID ? "Editar Formulário" : "Criar Formulário"}
                    </MuiButton>
                  </p>
                )}
              </div>

              {/* Documentos da Atividade */}
              {activityDocuments.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 text-left">
                    Documentos da Atividade
                  </h3>
                  <div className="grid grid-cols-4 gap-3">
                    {activityDocuments.map((docId: string, i: number) => {
                      const doc = props.documents?.find(
                        (d: FileData) => d._id === docId,
                      );
                      const safeDoc = doc || {
                        _id: docId,
                        name: "Documento",
                      };

                      const filledDoc = props.docsFilled?.[docId];

                      return (
                        <DocumentCard
                          key={docId}
                          doc={safeDoc}
                          index={i}
                          filledDoc={filledDoc}
                          isLoading={props.loadingUploads?.[docId]}
                          hasBindingQuestion={canUploadDocuments}
                          onDownload={handleDownload}
                          onUpload={handleUploadClick}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <hr />

              {/* Status */}
              <div className="flex gap-3">
                <button
                  onClick={() => setFilter("all")}
                  className="flex-1 border rounded-xl py-3"
                >
                  Total |{" "}
                  {selectForm?.formFields
                    ? selectForm?.formFields?.length
                    : selectForm?.data?.length}
                </button>
                <button
                  onClick={() => setFilter("filled")}
                  className="flex-1 border rounded-xl py-3 text-blue-600"
                >
                  Preenchidos |{" "}
                  {selectForm?.formFields
                    ? 0
                    : selectForm?.data?.filter((item) => item.data)?.length}
                </button>
                <button
                  onClick={() => setFilter("missing")}
                  className="flex-1 border rounded-xl py-3 text-red-500 flex justify-center gap-2 items-center"
                >
                  Faltantes:{" "}
                  {selectForm?.formFields
                    ? selectForm?.formFields?.length
                    : selectForm?.data?.filter((item) => !item.data)?.length}
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Pesquisar aqui..."
                  className="w-full border rounded-xl py-3 pl-12 pr-4"
                  value={search_2}
                  onChange={(e) => setSearch2(e.target.value)}
                />
              </div>

              {/* Lista ou Edição */}
              {isCreatingForm ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography sx={{ ml: 2, color: "text.secondary" }}>
                    Criando formulário...
                  </Typography>
                </Box>
              ) : isEditingForm ? (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">
                      {filledFormsID ? "Editar Formulário" : "Criar Formulário"}
                    </Typography>
                    <IconButton
                      onClick={() => setIsEditingForm(false)}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  {selectForm?._id && (
                    <FilledFormForm
                      id={filledFormsID}
                      hideHeader={true}
                      onSuccess={() => {
                        setIsEditingForm(false);
                        if (props.onReload) props.onReload();
                      }}
                    />
                  )}
                </Box>
              ) : (
                <div className="space-y-5  pr-2">
                  {props.loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 4 }}
                    >
                      <CircularProgress size={30} />
                    </Box>
                  ) : (
                    <>
                      {selectForm?.formFields &&
                        selectForm?.formFields
                          ?.filter((item) => item?.name?.includes(search_2))
                          ?.map((q, index) => (
                            <div key={q.id} className="space-y-1">
                              <div className="flex justify-between items-start gap-3">
                                <p
                                  className={`text-sm ${
                                    q.data ? "text-gray-500" : "text-red-500"
                                  }`}
                                >
                                  {String(index + 1).padStart(2, "0")} -{" "}
                                  {q.name}:
                                </p>
                                <div className="flex gap-2">
                                  {!q.data && (
                                    <Pencil
                                      hidden
                                      size={16}
                                      className="text-red-500"
                                    />
                                  )}
                                  <Copy
                                    hidden
                                    size={16}
                                    className="text-gray-400"
                                  />
                                </div>
                              </div>

                              {q.data && (
                                <p className="text-gray-900 font-medium">
                                  {q.data}
                                </p>
                              )}
                            </div>
                          ))}

                      {selectForm?.data &&
                        selectForm?.data
                          ?.filter((item) =>
                            item?.field?.name?.includes(search_2),
                          )
                          .map((q, index) => (
                            <div key={q.id} className="space-y-1">
                              {renderFieldValue(q.field, q.data, index)}
                              {/* <div className="flex justify-between items-start gap-3">
                    <p
                      className={`text-sm ${
                        q.data ? "text-gray-500" : "text-red-500"
                      }`}
                    >
                      {String(index+1).padStart(2, "0")} - {q?.field?.name}:
                    </p>
                    <div className="flex gap-2">
                      {!q.data && (
                        <Pencil  hidden size={16} className="text-red-500" />
                      )}
                      <Copy hidden size={16} className="text-gray-400" />
                    </div>
                  </div>

                  {q.data && (
                    <p className="text-gray-900 font-medium">{q.data}</p>
                  )} */}
                            </div>
                          ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
}
